import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { APIProvider, Map, AdvancedMarker, Pin, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Camera, Compass, Save, Upload, Trash2, MapPin, Image as ImageIcon, ImagePlus, X, Loader2, Move, Crosshair, Globe, Lock, Copy, Palette, FileDown, Video, Home, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, BarChart3, RotateCw, RotateCcw, ZoomIn, ZoomOut, Sparkles } from 'lucide-react';
import type { Hotspot, HotspotIcon, PlanosProject, PlanosFloor, LatLng, OverlayBounds, ViewMode, ViewerSettings } from '../types';
import { rotateImageToDataUrl, useGroundOverlay } from '../utils/floorPlanOverlay';
import { HOTSPOT_ICONS, HOTSPOT_COLORS } from '../constants';
import { updateProject, uploadImage, uploadFile, deleteProject } from '../services/supabase';
import { getDaysRemaining, formatDaysRemaining, getHostingStatusColor, canUserPublish, type CanUserPublishResult } from '../services/hosting';
import { getAnalyticsSummary, type PlanosAnalyticsSummary } from '../services/analytics';
import { detectRoomFromImage, imageUrlToBase64 } from '../services/gemini';
import HostingModal from './HostingModal';
import type { UserProfile } from '../types';

// NOTE: these are inlined by Vite at BUILD time. Changing the Vercel env vars
// requires a fresh deployment (a redeploy of the same commit is skipped by the
// Ignored Build Step) before the new value reaches the browser bundle.
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID || '';

/** 360° icon SVG — a sphere with "360" text */
function Icon360({ size = 12, className = 'text-white' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
      <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2.5 12h19" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

/** Renders hotspot icon in editor (floor plan markers + icon picker). Matches viewer. */
function EditorHotspotIcon({ type, iconUrl, size = 12 }: { type?: HotspotIcon; iconUrl?: string | null; size?: number }) {
  const t = type ?? 'camera';
  const props = { size, className: 'text-white' };
  if (t === 'custom' && iconUrl) {
    return <img src={iconUrl} alt="" className="object-contain" style={{ width: size, height: size }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />;
  }
  switch (t) {
    case 'pano360': return <Icon360 size={size} />;
    case 'file': return <FileDown {...props} />;
    case 'video': return <Video {...props} />;
    case 'map-pin': return <MapPin {...props} />;
    case 'home': return <Home {...props} />;
    default: return <Camera {...props} />;
  }
}

/* ─── Default overlay size around a center point; preserve image aspect to reduce warping ─────── */
function defaultBounds(center: LatLng, imageAspectRatio?: number): OverlayBounds {
  const baseLat = 0.0005;
  const baseLng = 0.0005;
  const aspect = imageAspectRatio ?? 1.5;
  const cosLat = Math.max(0.2, Math.cos((center.lat * Math.PI) / 180));
  const halfLat = baseLat;
  const halfLng = (baseLat * aspect) / cosLat;
  return {
    north: center.lat + halfLat,
    south: center.lat - halfLat,
    east: center.lng + halfLng,
    west: center.lng - halfLng,
  };
}

/** Normalize project to a floors array (from project.floors or legacy single floor). */
function getInitialFloors(project: PlanosProject): PlanosFloor[] {
  const arr = project.floors && project.floors.length > 0
    ? project.floors
    : project.floorplan_image_url
      ? [{ id: 'legacy', label: 'Ground', image_url: project.floorplan_image_url, overlay_bounds: project.overlay_bounds ?? defaultBounds(project.google_coords) }]
      : [{ id: crypto.randomUUID(), label: 'Ground', image_url: null, overlay_bounds: null }];
  return arr.map((f) => ({ ...f, id: f.id || crypto.randomUUID() }));
}

/* ─── Places Autocomplete search ─────────────────────────────────────────
 * Uses the new `PlaceAutocompleteElement` (Places API New). The legacy
 * `places.Autocomplete` widget was deprecated by Google (March 2025) and
 * fails on projects that don't have the legacy Places API enabled. The map
 * recenters via the `center` state + pan effect below, so this component
 * only needs to report the selected place upward. */
function AddressSearch({ onPlaceSelect }: { onPlaceSelect: (place: { lat: number; lng: number; address: string }) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const places = useMapsLibrary('places');
  // Keep the latest callback without re-instantiating the element each render.
  const onSelectRef = useRef(onPlaceSelect);
  onSelectRef.current = onPlaceSelect;

  useEffect(() => {
    const container = containerRef.current;
    const PlaceAutocompleteElement = (places as any)?.PlaceAutocompleteElement;
    if (!container || !PlaceAutocompleteElement) return;

    const el = new PlaceAutocompleteElement();
    el.style.width = '100%';
    try {
      el.setAttribute('placeholder', 'Search address or postcode...');
    } catch {
      /* placeholder unsupported on some builds — ignore */
    }
    container.appendChild(el);

    const handleSelect = async (event: any) => {
      const prediction = event?.placePrediction;
      if (!prediction) return;
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
      const loc = place.location;
      if (!loc) return;
      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      const address = place.formattedAddress || place.displayName || '';
      onSelectRef.current({ lat, lng, address });
    };

    el.addEventListener('gmp-select', handleSelect);
    return () => {
      el.removeEventListener('gmp-select', handleSelect);
      el.remove();
    };
  }, [places]);

  return <div ref={containerRef} className="planos-address-search relative w-full" style={{ colorScheme: 'dark' }} />;
}

/* ─── Radar cone SVG ─────────────────────────────────── */
const RadarCone: React.FC<{ heading: number }> = ({ heading }) => (
  <div className="absolute -left-5 -top-12 w-10 h-10" style={{ transform: `rotate(${heading}deg)` }}>
    <svg width="40" height="40" viewBox="0 0 40 40" className="opacity-60">
      <path d="M20 20 L38 10 A20 20 0 0 1 38 30 Z" fill="#ef4444" />
    </svg>
  </div>
);

/* ─── Editor ─────────────────────────────────────────── */
interface PlanosEditorProps {
  project: PlanosProject;
  user: UserProfile;
  onProjectUpdate: (p: PlanosProject) => void;
  onCreditsChange: (delta: number) => void;
  onProjectDelete?: () => void;
  onOpenCredits?: () => void;
  onOpenPlans?: () => void;
}

type EditorMode = 'hotspot' | 'set-center' | 'move-overlay';

const PlanosEditor: React.FC<PlanosEditorProps> = ({ project, user, onProjectUpdate, onCreditsChange, onProjectDelete, onOpenCredits, onOpenPlans }) => {
  const [hotspots, setHotspots] = useState<Hotspot[]>(project.hotspots ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);
  const [floors, setFloors] = useState<PlanosFloor[]>(() => getInitialFloors(project));
  const [selectedFloorId, setSelectedFloorId] = useState<string>(() => getInitialFloors(project)[0]?.id ?? '');
  const selectedFloor = floors.find((f) => f.id === selectedFloorId);
  const floorplanUrl = selectedFloor?.image_url ?? '';
  const overlayBounds = selectedFloor?.overlay_bounds ?? null;
  const [center, setCenter] = useState<LatLng>(project.google_coords);
  const [address, setAddress] = useState(project.address ?? '');
  const [projectTitle, setProjectTitle] = useState(project.title ?? '');
  const [internalJobNumber, setInternalJobNumber] = useState(project.internal_job_number ?? '');
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFloorplan, setUploadingFloorplan] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('hotspot');
  const [showHostingModal, setShowHostingModal] = useState(false);
  const [hostingModalSlotCheck, setHostingModalSlotCheck] = useState<CanUserPublishResult | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectPassword, setProjectPassword] = useState(project.password ?? '');
  const [embedMode, setEmbedMode] = useState<'responsive' | 'fixed'>('responsive');
  const [embedAspect, setEmbedAspect] = useState('56.25');
  const [embedWidth, setEmbedWidth] = useState('100%');
  const [embedHeight, setEmbedHeight] = useState('600');
  const [viewerSettings, setViewerSettings] = useState<ViewerSettings>(project.viewer_settings ?? {});
  const [mapHeading, setMapHeading] = useState<number>(project.map_heading ?? 0);
  const [showViewerSettings, setShowViewerSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsSummary, setAnalyticsSummary] = useState<PlanosAnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const floorplanInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const iconFileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const watermarkInputRef = useRef<HTMLInputElement>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingMedia, setPendingMedia] = useState<{ url: string; name: string }[]>([]);
  const [selectedPendingIndex, setSelectedPendingIndex] = useState<number | null>(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [floorplanSaveError, setFloorplanSaveError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameProgress, setRenameProgress] = useState({ current: 0, total: 0 });
  const [renameError, setRenameError] = useState<string | null>(null);

  const selected = hotspots.find((h) => h.id === selectedId) ?? null;
  const viewMode: ViewMode = project.view_mode ?? 'map';
  const isFloorplanMode = viewMode === 'floorplan';

  const updateSelectedFloor = useCallback((patch: Partial<Pick<PlanosFloor, 'image_url' | 'overlay_bounds'>>) => {
    if (!selectedFloorId) return;
    setFloors((prev) => prev.map((f) => (f.id === selectedFloorId ? { ...f, ...patch } : f)));
  }, [selectedFloorId]);

  // Rotate floor plan image by -mapHeading so it appears straight when the map is rotated
  const [overlayImageUrl, setOverlayImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!floorplanUrl) {
      setOverlayImageUrl(null);
      return;
    }
    let cancelled = false;
    rotateImageToDataUrl(floorplanUrl, -mapHeading).then((url) => {
      if (!cancelled) setOverlayImageUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [floorplanUrl, mapHeading]);

  const effectiveOverlayUrl =
    overlayImageUrl ?? (mapHeading === 0 ? (floorplanUrl || null) : null);
  useGroundOverlay(isFloorplanMode ? null : mapRef, effectiveOverlayUrl, overlayBounds);

  // Pan map live when center changes (address search, set-center click, or project load)
  useEffect(() => {
    if (mapRef && !isFloorplanMode) {
      mapRef.panTo(center);
      mapRef.setZoom(19);
    }
  }, [center, mapRef, isFloorplanMode]);

  // Sync from project only when switching project (by id). Avoids overwriting local state when parent updates after save.
  useEffect(() => {
    const initialFloors = getInitialFloors(project);
    setHotspots(project.hotspots ?? []);
    setBulkSelectedIds([]);
    setFloors(initialFloors);
    setSelectedFloorId(initialFloors[0]?.id ?? '');
    setCenter(project.google_coords);
    setAddress(project.address ?? '');
    setProjectTitle(project.title ?? '');
    setInternalJobNumber(project.internal_job_number ?? '');
    setProjectPassword(project.password ?? '');
    setFloorplanSaveError(null);
  }, [project.id]);

  useEffect(() => { setViewerSettings(project.viewer_settings ?? {}); }, [project.viewer_settings]);
  useEffect(() => { setProjectTitle(project.title ?? ''); setInternalJobNumber(project.internal_job_number ?? ''); }, [project.title, project.internal_job_number]);
  useEffect(() => { setMapHeading(project.map_heading ?? 0); }, [project.map_heading]);
  useEffect(() => {
    const hotspotIds = new Set(hotspots.map((h) => h.id));
    setBulkSelectedIds((prev) => prev.filter((id) => hotspotIds.has(id)));
  }, [hotspots]);

  useEffect(() => { setAnalyticsSummary(null); }, [project.id]);
  useEffect(() => {
    if (!showAnalytics) return;
    setAnalyticsLoading(true);
    getAnalyticsSummary(project.id).then((s) => { setAnalyticsSummary(s ?? null); setAnalyticsLoading(false); });
  }, [showAnalytics, project.id]);

  const handleFloorplanImageClick = useCallback((e: React.MouseEvent<HTMLDivElement> | React.PointerEvent<HTMLDivElement>) => {
    if (!floorplanUrl || !e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'clientX' in e ? e.clientX : e.nativeEvent?.clientX ?? 0;
    const clientY = 'clientY' in e ? e.clientY : e.nativeEvent?.clientY ?? 0;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    // If a hotspot is selected and no pending media is chosen, reposition the selected hotspot.
    if (selectedId && pendingMedia.length === 0) {
      setHotspots((prev) => prev.map((h) => (h.id === selectedId ? { ...h, x_pct: x, y_pct: y } : h)));
      return;
    }

    const idx = selectedPendingIndex ?? 0;
    const chosen = pendingMedia[idx] ?? null;
    const newHotspot: Hotspot = {
      id: crypto.randomUUID(),
      title: `Camera ${hotspots.length + 1}`,
      room_label: chosen ? `Room ${hotspots.length + 1}` : `Room ${hotspots.length + 1}`,
      lat: 0,
      lng: 0,
      photo_url: chosen?.url ?? null,
      heading: 90,
      x_pct: x,
      y_pct: y,
      icon_type: 'camera',
    };
    setHotspots((prev) => [...prev, newHotspot]);
    setSelectedId(newHotspot.id);
    if (chosen) {
      setPendingMedia((prev) => prev.filter((_, i) => i !== idx));
      setSelectedPendingIndex(null);
    }
  }, [floorplanUrl, hotspots.length, pendingMedia, selectedId, selectedPendingIndex]);

  const floorPlanPointerDown = useRef<{ x: number; y: number } | null>(null);
  const handleFloorPlanPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    floorPlanPointerDown.current = { x: e.clientX, y: e.clientY };
  }, []);
  const handleFloorPlanPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const down = floorPlanPointerDown.current;
    floorPlanPointerDown.current = null;
    if (!down) return;
    const dx = e.clientX - down.x;
    const dy = e.clientY - down.y;
    if (dx * dx + dy * dy > 25) return; // moved more than ~5px, treat as pan
    const synthetic = { ...e, clientX: e.clientX, clientY: e.clientY, currentTarget: e.currentTarget } as React.MouseEvent<HTMLDivElement>;
    handleFloorplanImageClick(synthetic);
  }, [handleFloorplanImageClick]);

  const handleMapClick = useCallback((event: any) => {
    if (isFloorplanMode) return;
    const latLng = event.detail?.latLng;
    if (!latLng) return;

    if (editorMode === 'set-center') {
      const newCenter = { lat: latLng.lat, lng: latLng.lng };
      setCenter(newCenter);
      if (overlayBounds) {
        const halfLat = (overlayBounds.north - overlayBounds.south) / 2;
        const halfLng = (overlayBounds.east - overlayBounds.west) / 2;
        updateSelectedFloor({
          overlay_bounds: { north: newCenter.lat + halfLat, south: newCenter.lat - halfLat, east: newCenter.lng + halfLng, west: newCenter.lng - halfLng },
        });
      }
      setEditorMode('hotspot');
      return;
    }

    if (editorMode === 'move-overlay' && overlayBounds) {
      const halfLat = (overlayBounds.north - overlayBounds.south) / 2;
      const halfLng = (overlayBounds.east - overlayBounds.west) / 2;
      updateSelectedFloor({
        overlay_bounds: { north: latLng.lat + halfLat, south: latLng.lat - halfLat, east: latLng.lng + halfLng, west: latLng.lng - halfLng },
      });
      setEditorMode('hotspot');
      return;
    }

    // If a hotspot is selected and no pending media is chosen, reposition the selected hotspot.
    if (selectedId && pendingMedia.length === 0) {
      setHotspots((prev) => prev.map((h) => (h.id === selectedId ? { ...h, lat: latLng.lat, lng: latLng.lng } : h)));
      return;
    }

    // Default: add hotspot (use selected from pending list, or first if none selected)
    const idx = selectedPendingIndex ?? 0;
    const chosen = pendingMedia[idx] ?? null;
    const newHotspot: Hotspot = {
      id: crypto.randomUUID(),
      title: `Camera ${hotspots.length + 1}`,
      room_label: `Room ${hotspots.length + 1}`,
      lat: latLng.lat,
      lng: latLng.lng,
      photo_url: chosen?.url ?? null,
      heading: 90,
      icon_type: 'camera',
    };
    setHotspots((prev) => [...prev, newHotspot]);
    setSelectedId(newHotspot.id);
    if (chosen) {
      setPendingMedia((prev) => prev.filter((_, i) => i !== idx));
      setSelectedPendingIndex(null);
    }
  }, [hotspots.length, editorMode, overlayBounds, isFloorplanMode, pendingMedia, selectedId, selectedPendingIndex, updateSelectedFloor]);

  const updateHotspot = (patch: Partial<Hotspot>) => {
    if (!selectedId) return;
    setHotspots((prev) => prev.map((h) => (h.id === selectedId ? { ...h, ...patch } : h)));
  };

  const deleteHotspot = () => {
    if (!selectedId) return;
    setHotspots((prev) => prev.filter((h) => h.id !== selectedId));
    setBulkSelectedIds((prev) => prev.filter((id) => id !== selectedId));
    setSelectedId(null);
  };

  const toggleBulkSelect = (id: string) => {
    setBulkSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllHotspots = () => {
    setBulkSelectedIds(hotspots.map((h) => h.id));
  };

  const clearBulkSelection = () => {
    setBulkSelectedIds([]);
  };

  const applyIconToBulkSelection = (type: HotspotIcon) => {
    if (bulkSelectedIds.length === 0) return;
    const selectedSet = new Set(bulkSelectedIds);
    setHotspots((prev) =>
      prev.map((h) =>
        selectedSet.has(h.id)
          ? { ...h, icon_type: type, ...(type === 'custom' ? {} : { icon_url: null }) }
          : h
      )
    );
  };

  const applyColorToBulkSelection = (color: string | null) => {
    if (bulkSelectedIds.length === 0) return;
    const selectedSet = new Set(bulkSelectedIds);
    setHotspots((prev) =>
      prev.map((h) => (selectedSet.has(h.id) ? { ...h, icon_color: color } : h))
    );
  };

  const handlePlaceSelect = useCallback(async (place: { lat: number; lng: number; address: string }) => {
    const newCenter = { lat: place.lat, lng: place.lng };
    setCenter(newCenter);
    setAddress(place.address);
    if (overlayBounds) {
      const halfLat = (overlayBounds.north - overlayBounds.south) / 2;
      const halfLng = (overlayBounds.east - overlayBounds.west) / 2;
      updateSelectedFloor({ overlay_bounds: { north: newCenter.lat + halfLat, south: newCenter.lat - halfLat, east: newCenter.lng + halfLng, west: newCenter.lng - halfLng } });
    }
    const { data } = await updateProject(project.id, { google_coords: newCenter, address: place.address });
    if (data) onProjectUpdate(data);
  }, [project.id, overlayBounds, onProjectUpdate, updateSelectedFloor]);

  const handleFloorplanUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploadingFloorplan(true);
    let imageAspect: number | undefined;
    try {
      const blobUrl = URL.createObjectURL(file);
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(blobUrl); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(); };
        img.src = blobUrl;
      });
      imageAspect = dims.w / dims.h;
    } catch {
      imageAspect = undefined;
    }
    const url = await uploadImage(user.id, file, 'planos-floorplans');
    if (url && selectedFloorId) {
      const bounds = overlayBounds ?? defaultBounds(center, imageAspect);
      const nextFloors = floors.map((f) => (f.id === selectedFloorId ? { ...f, image_url: url, overlay_bounds: bounds } : f));
      setFloors(nextFloors);
      const first = nextFloors[0];
      const { data, error } = await updateProject(project.id, {
        floors: nextFloors,
        floorplan_image_url: first?.image_url ?? null,
        overlay_bounds: first?.overlay_bounds ?? null,
      });
      if (data) {
        onProjectUpdate(data);
        setFloorplanSaveError(null);
      } else {
        setFloorplanSaveError(error ?? 'Floor plan did not save. Click Save below to retry.');
      }
    }
    setUploadingFloorplan(false);
  };

  const handleFloorplanDrop = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer.files[0]; if (file) handleFloorplanUpload(file); };
  const handleFloorplanInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) handleFloorplanUpload(file); e.target.value = ''; };

  const removeFloorplan = async () => {
    if (!selectedFloorId) return;
    const nextFloors = floors.map((f) => (f.id === selectedFloorId ? { ...f, image_url: null, overlay_bounds: null } : f));
    setFloors(nextFloors);
    const first = nextFloors[0];
    const { data, error } = await updateProject(project.id, {
      floors: nextFloors,
      floorplan_image_url: first?.image_url ?? null,
      overlay_bounds: first?.overlay_bounds ?? null,
    });
    if (data) {
      onProjectUpdate(data);
      setFloorplanSaveError(null);
    } else {
      setFloorplanSaveError(error ?? 'Remove did not save. Click Save to retry.');
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    const url = await uploadImage(user.id, file, 'planos-hotspots');
    if (url) updateHotspot({ photo_url: url });
  };

  const handleCustomIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    setUploadingIcon(true);
    const url = await uploadImage(user.id, file, 'planos-hotspots');
    if (url) updateHotspot({ icon_type: 'custom', icon_url: url });
    setUploadingIcon(false);
    e.target.value = '';
  };

  const BULK_UPLOAD_CONCURRENCY = 5;

  const processBulkFiles = async (imageFiles: File[]) => {
    if (!imageFiles.length) return;
    setUploadingBulk(true);
    setBulkProgress({ current: 0, total: imageFiles.length });
    const next: { url: string; name: string }[] = [];
    for (let i = 0; i < imageFiles.length; i += BULK_UPLOAD_CONCURRENCY) {
      const chunk = imageFiles.slice(i, i + BULK_UPLOAD_CONCURRENCY);
      const results = await Promise.all(
        chunk.map(async (file, j) => {
          const url = await uploadImage(user.id, file, 'planos-hotspots');
          return url ? { url, name: file.name || `Photo ${i + j + 1}` } : null;
        })
      );
      results.filter((r): r is { url: string; name: string } => r != null).forEach((r) => next.push(r));
      setBulkProgress({ current: Math.min(i + chunk.length, imageFiles.length), total: imageFiles.length });
    }
    setPendingMedia((prev) => [...prev, ...next]);
    setUploadingBulk(false);
    setBulkProgress({ current: 0, total: 0 });
  };

  const handleBulkPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    await processBulkFiles(imageFiles);
    e.target.value = '';
  };

  const [bulkDragOver, setBulkDragOver] = useState(false);

  const handleBulkDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setBulkDragOver(false);
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    await processBulkFiles(imageFiles);
  };

  const removePendingMedia = (index: number) => {
    setPendingMedia((prev) => prev.filter((_, i) => i !== index));
    setSelectedPendingIndex((prev) => {
      if (prev == null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const clearPendingMedia = () => {
    setPendingMedia([]);
    setSelectedPendingIndex(null);
  };

  const handleRenameRooms = async () => {
    const withPhotos = hotspots.filter((h) => h.photo_url);
    if (withPhotos.length === 0) {
      setRenameError('No hotspots with photos to rename.');
      return;
    }
    setRenaming(true);
    setRenameError(null);
    setRenameProgress({ current: 0, total: withPhotos.length });
    const errors: string[] = [];
    const updates = new Map<string, Partial<Hotspot>>();

    for (let i = 0; i < withPhotos.length; i++) {
      const h = withPhotos[i];
      setRenameProgress({ current: i + 1, total: withPhotos.length });
      try {
        const base64 = await imageUrlToBase64(h.photo_url!);
        const result = await detectRoomFromImage(base64);
        updates.set(h.id, { room_label: result.room_label, title: result.room_label });
      } catch (err: any) {
        errors.push(`"${h.room_label}": ${err.message}`);
      }
    }

    if (updates.size > 0) {
      setHotspots((prev) =>
        prev.map((h) => {
          const patch = updates.get(h.id);
          return patch ? { ...h, ...patch } : h;
        })
      );
    }

    setRenaming(false);
    setRenameProgress({ current: 0, total: 0 });
    if (errors.length > 0) {
      setRenameError(`Renamed ${updates.size}/${withPhotos.length}. Errors: ${errors.join('; ')}`);
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return;
    setUploadingPdf(true);
    const url = await uploadFile(user.id, file, 'planos-downloads');
    if (url) updateHotspot({ download_url: url });
    setUploadingPdf(false);
    e.target.value = '';
  };

  const resizeOverlay = (factor: number) => {
    if (!overlayBounds) return;
    const cLat = (overlayBounds.north + overlayBounds.south) / 2;
    const cLng = (overlayBounds.east + overlayBounds.west) / 2;
    const halfLat = ((overlayBounds.north - overlayBounds.south) / 2) * factor;
    const halfLng = ((overlayBounds.east - overlayBounds.west) / 2) * factor;
    updateSelectedFloor({ overlay_bounds: { north: cLat + halfLat, south: cLat - halfLat, east: cLng + halfLng, west: cLng - halfLng } });
  };

  const NUDGE = 0.00003;
  const nudgeOverlay = (dir: 'n' | 's' | 'e' | 'w') => {
    if (!overlayBounds) return;
    const d = NUDGE;
    updateSelectedFloor({
      overlay_bounds: {
        north: overlayBounds.north + (dir === 'n' ? d : dir === 's' ? -d : 0),
        south: overlayBounds.south + (dir === 's' ? d : dir === 'n' ? -d : 0),
        east: overlayBounds.east + (dir === 'e' ? d : dir === 'w' ? -d : 0),
        west: overlayBounds.west + (dir === 'w' ? d : dir === 'e' ? -d : 0),
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFloorplanSaveError(null);
    const first = floors[0];
    const { data, error } = await updateProject(project.id, {
      title: projectTitle.trim() || 'Untitled Plan',
      internal_job_number: internalJobNumber.trim() || null,
      hotspots,
      floors,
      floorplan_image_url: first?.image_url ?? null,
      overlay_bounds: first?.overlay_bounds ?? null,
      google_coords: center,
      address: address || null,
      map_heading: mapHeading,
      view_mode: viewMode,
      viewer_settings: Object.keys(viewerSettings).length ? viewerSettings : undefined,
    });
    if (data) {
      onProjectUpdate(data);
    } else {
      setFloorplanSaveError(error ?? 'Save failed. Check your connection and try again.');
    }
    setSaving(false);
  };

  const handleViewerSettingsUpdate = (patch: Partial<ViewerSettings>) => {
    setViewerSettings((s) => ({ ...s, ...patch }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const url = await uploadImage(user.id, file, 'planos-logos');
    if (url) handleViewerSettingsUpdate({ logo_url: url });
    e.target.value = '';
  };

  const handleWatermarkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const url = await uploadImage(user.id, file, 'planos-logos');
    if (url) handleViewerSettingsUpdate({ logo_watermark_url: url });
    e.target.value = '';
  };

  const handleDelete = async () => {
    const ok = await deleteProject(project.id);
    if (ok && onProjectDelete) onProjectDelete();
  };

  const slug = project.slug ?? project.id;
  const daysLeft = getDaysRemaining(project.hosting_expires_at ?? null, project.hosting_permanent);
  const hostColor = getHostingStatusColor(project.hosting_status ?? 'inactive', daysLeft);
  const isHosted = (project.hosting_status ?? 'inactive') === 'active' || project.hosting_permanent;
  const isExpired = (project.hosting_status ?? 'inactive') === 'expired' && !project.hosting_permanent;

  const copyShareLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/view/${slug}`);
  };
  const getEmbedCode = () => {
    const url = `${window.location.origin}/embed/${slug}`;
    if (embedMode === 'fixed') {
      const w = embedWidth.includes('%') ? embedWidth : `${embedWidth}px`;
      return `<iframe src="${url}" width="${w}" height="${embedHeight}px" style="border:0;border-radius:12px;" allowfullscreen></iframe>`;
    }
    return `<div style="position:relative;width:100%;padding-bottom:${embedAspect}%;border-radius:12px;overflow:hidden;"><iframe src="${url}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
  };
  const copyEmbedCode = () => navigator.clipboard.writeText(getEmbedCode());

  const handleSavePassword = async () => {
    const { data } = await updateProject(project.id, { password: projectPassword.trim() || null });
    if (data) onProjectUpdate(data);
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0 p-3 sm:p-4 overflow-hidden">
      {/* Sidebar: on mobile cap height so map remains visible; on lg+ fixed width */}
      <div className="w-full lg:w-80 shrink-0 rounded-2xl border p-4 overflow-y-auto space-y-4 max-h-[50vh] lg:max-h-none flex-shrink-0" style={{ backgroundColor: '#2F2F2F', borderColor: '#3d3d3d' }}>
        <h2 className="text-sm font-semibold text-white flex items-center gap-2"><MapPin size={16} style={{ color: '#FF3B30' }} /> Editor Controls</h2>

        {/* Project title */}
        <div className="space-y-1">
          <label className="text-xs text-slate-400">Project name</label>
          <input
            value={projectTitle}
            onChange={(e) => setProjectTitle(e.target.value)}
            placeholder="Untitled Plan"
            className="w-full px-3 py-2 bg-black border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none"
          />
        </div>
        {/* Internal job number */}
        <div className="space-y-1">
          <label className="text-xs text-slate-400">Job number</label>
          <input
            value={internalJobNumber}
            onChange={(e) => setInternalJobNumber(e.target.value)}
            placeholder="e.g. PL-001"
            className="w-full px-3 py-2 bg-black border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none"
          />
        </div>

        {/* Map location — both floor plan and map overlay */}
        <div className="space-y-1">
          <label className="text-xs text-slate-400">Map location</label>
          {MAPS_KEY && (
            <APIProvider apiKey={MAPS_KEY} libraries={['places']}>
              <AddressSearch onPlaceSelect={handlePlaceSelect} />
            </APIProvider>
          )}
          {address && <p className="text-[10px] text-slate-500 truncate">{address}</p>}
          {!isFloorplanMode && (editorMode === 'set-center' || editorMode === 'move-overlay') && (
            <p className="text-[10px] text-amber-400">Click map to set location, then Save below.</p>
          )}
        </div>

        {/* Editor mode selector — only for map mode */}
        {!isFloorplanMode && (
          <>
            <div className="flex gap-1">
              <button onClick={() => setEditorMode('hotspot')} className={`flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors ${editorMode === 'hotspot' ? 'bg-[#FF3B30] text-white' : 'bg-black/30 text-slate-400 hover:text-white'}`}>
                <Camera size={12} /> Hotspot
              </button>
              <button onClick={() => setEditorMode('set-center')} className={`flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors ${editorMode === 'set-center' ? 'bg-[#FF3B30] text-white' : 'bg-black/30 text-slate-400 hover:text-white'}`}>
                <Crosshair size={12} /> Center
              </button>
              <button onClick={() => setEditorMode('move-overlay')} disabled={!floorplanUrl} className={`flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-30 ${editorMode === 'move-overlay' ? 'bg-[#FF3B30] text-white' : 'bg-black/30 text-slate-400 hover:text-white'}`}>
                <Move size={12} /> Overlay
              </button>
            </div>
            {editorMode === 'set-center' && <p className="text-[10px] text-yellow-400">Click the map to set the property center point.</p>}
            {editorMode === 'move-overlay' && <p className="text-[10px] text-yellow-400">Click the map to move the floor plan overlay.</p>}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500 shrink-0">Rotate map:</span>
              <button onClick={() => setMapHeading((h) => (h - 5 + 360) % 360)} className="p-1.5 rounded-lg bg-black/40 text-slate-300 hover:bg-black/60 transition-colors" title="Rotate map left 5°"><RotateCcw size={14} /></button>
              <span className="text-xs text-slate-400 w-8 text-center">{Math.round(mapHeading)}°</span>
              <button onClick={() => setMapHeading((h) => (h + 5) % 360)} className="p-1.5 rounded-lg bg-black/40 text-slate-300 hover:bg-black/60 transition-colors" title="Rotate map right 5°"><RotateCw size={14} /></button>
            </div>
            <p className="text-[10px] text-slate-500">Align map orientation with your floor plan before placing it.</p>
            <button onClick={handleSave} disabled={saving} className="w-full py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 bg-black/40 text-slate-300 hover:bg-[#FF3B30] hover:text-white transition-colors" title="Save map location">
              <Save size={14} /> Save map location
            </button>
          </>
        )}

        {isFloorplanMode && <p className="text-[10px] text-slate-500">Upload a floor plan, then click on it to add hotspots.</p>}

        {/* Floor plan upload (single floor in planos — no multi-floor selector) */}
        <div className="space-y-2">
          <label className="text-xs text-slate-400">Floor plan</label>
          {floorplanUrl ? (
            <div className="space-y-2">
              <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: '#3d3d3d' }}>
                <img src={floorplanUrl} alt="Floor plan" className="w-full h-36 object-contain bg-black" />
                <button onClick={removeFloorplan} className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-slate-300 hover:text-white hover:bg-red-600/80 transition-colors" title="Remove floor plan"><X size={14} /></button>
              </div>
              {/* Overlay size & nudge controls — only for map mode */}
              {!isFloorplanMode && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500">Overlay size:</span>
                    <button onClick={() => resizeOverlay(0.85)} className="px-2 py-0.5 rounded bg-black/40 text-xs text-slate-300 hover:bg-black/60">-</button>
                    <button onClick={() => resizeOverlay(1.18)} className="px-2 py-0.5 rounded bg-black/40 text-xs text-slate-300 hover:bg-black/60">+</button>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-slate-500">Nudge overlay:</span>
                    <div className="flex justify-center">
                      <button onClick={() => nudgeOverlay('n')} className="p-1 rounded bg-black/40 text-slate-300 hover:bg-black/60" title="North"><ChevronUp size={14} /></button>
                    </div>
                    <div className="flex justify-center gap-0.5">
                      <button onClick={() => nudgeOverlay('w')} className="p-1 rounded bg-black/40 text-slate-300 hover:bg-black/60" title="West"><ChevronLeft size={14} /></button>
                      <span className="w-6 text-[10px] text-slate-600 flex items-center justify-center">◉</span>
                      <button onClick={() => nudgeOverlay('e')} className="p-1 rounded bg-black/40 text-slate-300 hover:bg-black/60" title="East"><ChevronRight size={14} /></button>
                    </div>
                    <div className="flex justify-center">
                      <button onClick={() => nudgeOverlay('s')} className="p-1 rounded bg-black/40 text-slate-300 hover:bg-black/60" title="South"><ChevronDown size={14} /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFloorplanDrop}
              onClick={() => !uploadingFloorplan && floorplanInputRef.current?.click()}
              className={`w-full py-6 border-2 border-dashed rounded-xl text-center cursor-pointer transition-colors ${dragOver ? 'border-[#FF3B30] bg-[#FF3B30]/10' : 'border-slate-600 hover:border-[#FF3B30]/50 hover:bg-white/5'}`}
            >
              {uploadingFloorplan ? (
                <div className="flex flex-col items-center gap-2"><Loader2 size={20} className="animate-spin text-[#FF3B30]" /><span className="text-xs text-slate-400">Uploading...</span></div>
              ) : (
                <div className="flex flex-col items-center gap-2"><ImageIcon size={24} className="text-slate-500" /><span className="text-xs text-slate-400">Drop floor plan here or click to browse</span><span className="text-[10px] text-slate-600">PNG, JPG, or WebP</span></div>
              )}
            </div>
          )}
          <input ref={floorplanInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFloorplanInputChange} />
          {floorplanSaveError && (
            <p className="text-xs text-amber-400 flex items-center gap-1.5">
              <span className="shrink-0">⚠</span>
              {floorplanSaveError}
            </p>
          )}
        </div>

        {/* Selected hotspot panel */}
        {selected ? (
          <div className="space-y-2 p-3 rounded-xl border" style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderColor: '#3d3d3d' }}>
            <p className="text-xs uppercase tracking-wide text-slate-400">Selected hotspot</p>
            <input value={selected.room_label} onChange={(e) => updateHotspot({ room_label: e.target.value })} placeholder="Room name" className="w-full px-3 py-2 bg-black border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none" />
            <div>
              <span className="text-[10px] text-slate-500 block mb-1">Icon</span>
              <div className="flex gap-1 flex-wrap">
                {HOTSPOT_ICONS.map(({ type }) => (
                  <button key={type} onClick={() => updateHotspot({ icon_type: type })} className={`p-1.5 rounded border transition-colors ${(selected.icon_type ?? 'camera') === type ? 'border-[#FF3B30] bg-[#FF3B30]/20' : 'border-slate-600 hover:border-slate-500 bg-black/40'}`} title={HOTSPOT_ICONS.find((i) => i.type === type)?.label}>
                    {type === 'camera' && <Camera size={14} className="text-slate-300" />}
                    {type === 'pano360' && <Icon360 size={14} className="text-slate-300" />}
                    {type === 'custom' && (selected.icon_url ? <img src={selected.icon_url} alt="" className="w-3.5 h-3.5 object-contain rounded-sm" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <span className="text-slate-400 text-[10px]">+</span>)}
                    {type === 'file' && <FileDown size={14} className="text-slate-300" />}
                    {type === 'video' && <Video size={14} className="text-slate-300" />}
                    {type === 'map-pin' && <MapPin size={14} className="text-slate-300" />}
                    {type === 'home' && <Home size={14} className="text-slate-300" />}
                  </button>
                ))}
              </div>
              {(selected.icon_type ?? 'camera') === 'custom' && (
                <div className="mt-1.5 space-y-1">
                  <button type="button" onClick={() => iconFileInputRef.current?.click()} className="w-full py-1.5 border border-dashed border-slate-600 rounded text-[10px] text-slate-400 hover:border-[#FF3B30]/50 hover:text-white transition-colors flex items-center justify-center gap-1">
                    {uploadingIcon ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploadingIcon ? 'Uploading...' : 'Upload custom icon'}
                  </button>
                  <input ref={iconFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleCustomIconUpload} />
                  <input value={selected.icon_url ?? ''} onChange={(e) => updateHotspot({ icon_url: e.target.value.trim() || null })} placeholder="Or paste image URL" className="w-full px-2 py-1 bg-black border border-slate-700 rounded text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none" />
                </div>
              )}
            </div>
            <div>
              <span className="text-[10px] text-slate-500 block mb-1">Marker colour</span>
              <div className="flex gap-1 flex-wrap items-center">
                {HOTSPOT_COLORS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateHotspot({ icon_color: value })}
                    className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${(selected.icon_color ?? '#FF3B30') === value ? 'border-white scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: value }}
                    title={label}
                  />
                ))}
                <input
                  type="color"
                  value={selected.icon_color ?? '#FF3B30'}
                  onChange={(e) => updateHotspot({ icon_color: e.target.value })}
                  className="w-5 h-5 rounded cursor-pointer border border-slate-600"
                  title="Custom colour"
                />
                {selected.icon_color && (
                  <button type="button" onClick={() => updateHotspot({ icon_color: null })} className="text-[10px] text-slate-500 hover:text-white ml-1">Reset</button>
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Download (PDF / brochure)</label>
              {selected.download_url ? (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-black/40 border border-slate-700">
                  <FileDown size={14} className="text-green-400 shrink-0" />
                  <span className="flex-1 text-xs text-slate-300 truncate">{selected.download_url.includes('/planos-downloads/') ? 'PDF hosted' : 'Link set'}</span>
                  <button onClick={() => updateHotspot({ download_url: null })} className="text-[10px] text-red-400 hover:text-red-300">Remove</button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <button onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf} className="w-full py-2 border border-dashed border-slate-600 rounded-lg text-xs text-slate-400 hover:border-[#FF3B30]/50 hover:text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50">
                    {uploadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingPdf ? 'Uploading...' : 'Upload PDF'}
                  </button>
                  <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handlePdfUpload} />
                  <input value={selected.download_url ?? ''} onChange={(e) => updateHotspot({ download_url: e.target.value.trim() || null })} placeholder="Or paste URL" className="w-full px-2 py-1.5 bg-black border border-slate-700 rounded text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none" />
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Video (YouTube, Vimeo, etc.)</label>
              <input value={selected.video_url ?? ''} onChange={(e) => updateHotspot({ video_url: e.target.value.trim() || null })} placeholder="https://youtube.com/watch?v=..." className="w-full px-2 py-1.5 bg-black border border-slate-700 rounded text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none" />
            </div>
            {!isFloorplanMode && (
              <div className="flex items-center gap-2">
                <Compass size={14} className="text-slate-400" />
                <input type="number" min={0} max={359} value={selected.heading} onChange={(e) => updateHotspot({ heading: Number(e.target.value) || 0 })} className="flex-1 px-3 py-2 bg-black border border-slate-700 rounded-lg text-sm text-white focus:ring-2 focus:ring-[#FF3B30]/50 focus:outline-none" />
                <span className="text-xs text-slate-500">deg</span>
              </div>
            )}
            {selected.photo_url ? (
              <img src={selected.photo_url} alt="Hotspot" className="w-full h-32 object-cover rounded-lg" />
            ) : (
              <button onClick={() => fileInputRef.current?.click()} className="w-full py-2 border border-dashed border-slate-600 rounded-lg text-xs text-slate-400 hover:border-[#FF3B30]/50 hover:text-white transition-colors flex items-center justify-center gap-1.5">
                <Upload size={14} /> Attach photo
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            <button onClick={deleteHotspot} className="w-full py-2 text-xs text-red-400 hover:text-red-300 flex items-center justify-center gap-1"><Trash2 size={12} /> Remove hotspot</button>
          </div>
        ) : (
          <p className="text-xs text-slate-400">{isFloorplanMode ? 'Select an image above, then click the floor plan to add a hotspot.' : editorMode === 'hotspot' ? 'Select an image above, then click the map to place.' : ''}</p>
        )}

        {/* Bulk upload photos / 360 */}
        <div className="space-y-2 pt-2 border-t" style={{ borderColor: '#3d3d3d' }}>
          <label className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
            <ImagePlus size={14} />
            Bulk upload
          </label>
          <p className="text-[10px] text-slate-500">Drop or select photos/360s, then click an image below to choose it and click the map or floor plan to place.</p>
          <input ref={bulkFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleBulkPhotoUpload} />
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (!uploadingBulk && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); bulkFileInputRef.current?.click(); } }}
            onClick={() => { if (!uploadingBulk) bulkFileInputRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploadingBulk) setBulkDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setBulkDragOver(false); }}
            onDrop={handleBulkDrop}
            className={`w-full py-4 px-3 border-2 border-dashed rounded-xl text-center transition-colors flex flex-col items-center justify-center gap-1.5 min-h-[88px] cursor-pointer select-none ${uploadingBulk ? 'border-slate-600 bg-black/30 cursor-wait' : bulkDragOver ? 'border-[#FF3B30]/60 bg-[#FF3B30]/10' : 'border-slate-600 bg-black/20 hover:border-[#FF3B30]/50 hover:bg-black/30'}`}
          >
            {uploadingBulk ? (
              <>
                <Loader2 size={20} className="animate-spin text-[#FF3B30]" />
                <span className="text-sm text-slate-300">Uploading {bulkProgress.current} of {bulkProgress.total}…</span>
              </>
            ) : bulkDragOver ? (
              <span className="text-sm font-medium text-[#FF3B30]">Drop images here</span>
            ) : (
              <>
                <Upload size={20} className="text-slate-400" />
                <span className="text-xs text-slate-400">Drop here or click to select</span>
                <span className="mt-0.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FF3B30] text-white">
                  Select photos / 360s
                </span>
              </>
            )}
          </div>
          {pendingMedia.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Pending ({pendingMedia.length})</span>
                <button type="button" onClick={clearPendingMedia} className="text-[10px] text-slate-500 hover:text-red-400">Clear all</button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 min-h-[88px]">
                {pendingMedia.map((item, index) => (
                  <div
                    key={`${item.url}-${index}`}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { if ((e.target as HTMLElement).closest('button')) return; setSelectedPendingIndex((prev) => (prev === index ? null : index)); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPendingIndex((prev) => (prev === index ? null : index)); } }}
                    className={`relative shrink-0 group cursor-pointer rounded border-2 transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FF3B30]/50 ${selectedPendingIndex === index ? 'border-[#FF3B30] ring-2 ring-[#FF3B30]/40' : 'border-slate-600 hover:border-slate-500'}`}
                    title={item.name}
                  >
                    <img src={item.url} alt={item.name} className="w-20 h-20 object-cover rounded-sm pointer-events-none" />
                    <span className="absolute top-0 left-0 w-5 h-5 rounded-br bg-black/80 text-xs text-white flex items-center justify-center font-medium">{index + 1}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); removePendingMedia(index); }} className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove from list">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Room list */}
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-slate-300">Room list</p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleRenameRooms}
                disabled={renaming || hotspots.filter((h) => h.photo_url).length === 0}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Use AI to name rooms from their photos"
              >
                {renaming ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                {renaming ? `Renaming ${renameProgress.current}/${renameProgress.total}…` : 'AI Rename'}
              </button>
              <span className="text-[10px] text-slate-500">{bulkSelectedIds.length} sel</span>
              <button
                type="button"
                onClick={selectAllHotspots}
                disabled={hotspots.length === 0}
                className="text-[10px] text-slate-400 hover:text-white disabled:opacity-40"
              >
                All
              </button>
              <button
                type="button"
                onClick={clearBulkSelection}
                disabled={bulkSelectedIds.length === 0}
                className="text-[10px] text-slate-400 hover:text-white disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>
          {renameError && (
            <p className="text-[10px] text-amber-400 mb-1">{renameError}</p>
          )}
          {bulkSelectedIds.length > 0 && (
            <div className="mb-2 p-2 rounded-lg border border-slate-700 bg-black/30">
              <p className="text-[10px] text-slate-500 mb-1.5">Apply icon to selected</p>
              <div className="flex gap-1 flex-wrap">
                {HOTSPOT_ICONS.filter(({ type }) => type !== 'custom').map(({ type }) => (
                  <button
                    key={`bulk-${type}`}
                    type="button"
                    onClick={() => applyIconToBulkSelection(type)}
                    className="p-1.5 rounded border border-slate-600 hover:border-[#FF3B30] bg-black/40 transition-colors"
                    title={`Set selected to ${type}`}
                  >
                    {type === 'camera' && <Camera size={14} className="text-slate-300" />}
                    {type === 'pano360' && <Icon360 size={14} className="text-slate-300" />}
                    {type === 'file' && <FileDown size={14} className="text-slate-300" />}
                    {type === 'video' && <Video size={14} className="text-slate-300" />}
                    {type === 'map-pin' && <MapPin size={14} className="text-slate-300" />}
                    {type === 'home' && <Home size={14} className="text-slate-300" />}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-2 mb-1.5">Apply colour to selected</p>
              <div className="flex gap-1 flex-wrap items-center">
                {HOTSPOT_COLORS.map(({ value, label }) => (
                  <button
                    key={`bulk-c-${value}`}
                    type="button"
                    onClick={() => applyColorToBulkSelection(value)}
                    className="w-5 h-5 rounded-full border-2 border-transparent hover:border-white transition-transform hover:scale-110"
                    style={{ backgroundColor: value }}
                    title={label}
                  />
                ))}
                <button type="button" onClick={() => applyColorToBulkSelection(null)} className="text-[10px] text-slate-500 hover:text-white ml-1">Reset</button>
              </div>
            </div>
          )}
          {hotspots.map((h) => {
            const IconEl = (() => { const t = h.icon_type ?? 'camera'; switch (t) { case 'pano360': return () => <Icon360 size={14} className="text-slate-500" />; case 'file': return FileDown; case 'video': return Video; case 'map-pin': return MapPin; case 'home': return Home; default: return Camera; } })();
            return (
              <div key={h.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${h.id === selectedId ? 'bg-[#FF3B30]/20 text-white' : 'bg-black/30 text-slate-300 hover:bg-black/50'}`}>
                <input
                  type="checkbox"
                  checked={bulkSelectedIds.includes(h.id)}
                  onChange={() => toggleBulkSelect(h.id)}
                  className="h-3.5 w-3.5 rounded border-slate-500 bg-black text-[#FF3B30] focus:ring-[#FF3B30]/60"
                  aria-label={`Select ${h.room_label}`}
                />
                <button
                  type="button"
                  onClick={() => { setSelectedId(h.id); if (!isFloorplanMode) mapRef?.panTo({ lat: h.lat, lng: h.lng }); }}
                  className="flex flex-1 items-center justify-between text-left text-sm gap-1.5"
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: h.icon_color ?? '#dc2626' }} />
                  <span className="truncate flex-1">{h.room_label}</span>
                  <IconEl size={14} className="text-slate-500 shrink-0" />
                </button>
              </div>
            );
          })}
          {hotspots.length === 0 && <p className="text-[10px] text-slate-500">No hotspots yet.</p>}
        </div>

        {/* Share & Publish */}
        <div className="space-y-3 pt-2 border-t" style={{ borderColor: '#3d3d3d' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
              <Globe size={14} className={hostColor.text} />
              {isHosted ? 'Live' : isExpired ? 'Expired' : 'Draft'}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${hostColor.bg} ${hostColor.text}`}>
              {project.hosting_permanent ? 'Permanent' : daysLeft != null ? formatDaysRemaining(daysLeft) : 'No hosting'}
            </span>
          </div>
          <button
            onClick={async () => {
              await handleSave();
              const slotCheck = await canUserPublish(user.id, user);
              setHostingModalSlotCheck(slotCheck);
              setShowHostingModal(true);
            }}
            className={`w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${isHosted ? 'bg-black/40 text-slate-300 hover:bg-black/60' : 'bg-[#FF3B30] text-white hover:opacity-90'}`}
          >
            <Globe size={14} />
            {isHosted ? 'Extend Publishing' : isExpired ? 'Republish' : 'Publish'}
          </button>
          {isFloorplanMode && !floorplanUrl && (
            <p className="text-[10px] text-amber-400/90">Upload a floor plan above, then Publish so the public link shows it.</p>
          )}

          <div>
            <label className="block text-[10px] text-slate-500 mb-1">Share link</label>
            <div className="flex gap-1">
              <input readOnly value={`${typeof window !== 'undefined' ? window.location.origin : ''}/view/${slug}`} className="flex-1 px-2 py-1.5 bg-black border border-slate-700 rounded text-[10px] text-slate-300 truncate" />
              <button onClick={copyShareLink} className="p-1.5 rounded bg-black/40 hover:bg-black/60 text-slate-300" title="Copy"><Copy size={12} /></button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] text-slate-500 mb-1">Embed code</label>
            <div className="flex gap-1 mb-1">
              <button onClick={() => setEmbedMode('responsive')} className={`flex-1 py-1 rounded text-[10px] font-medium ${embedMode === 'responsive' ? 'bg-[#FF3B30] text-white' : 'bg-black/40 text-slate-400'}`}>Responsive</button>
              <button onClick={() => setEmbedMode('fixed')} className={`flex-1 py-1 rounded text-[10px] font-medium ${embedMode === 'fixed' ? 'bg-[#FF3B30] text-white' : 'bg-black/40 text-slate-400'}`}>Fixed</button>
            </div>
            <pre className="p-2 bg-black border border-slate-700 rounded text-[9px] text-slate-400 overflow-x-auto whitespace-pre-wrap break-all max-h-20 overflow-y-auto">{getEmbedCode()}</pre>
            <button onClick={copyEmbedCode} className="mt-1 w-full py-1 rounded text-[10px] text-slate-400 hover:text-white flex items-center justify-center gap-1"><Copy size={10} /> Copy</button>
          </div>

          <div>
            <label className="block text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Lock size={10} /> Password</label>
            <input type="text" value={projectPassword} onChange={(e) => setProjectPassword(e.target.value)} onBlur={handleSavePassword} placeholder="Leave blank for public" className="w-full px-2 py-1.5 bg-black border border-slate-700 rounded text-xs text-white placeholder-slate-500" />
          </div>

          {/* Analytics */}
          <div className="space-y-2">
            <button type="button" onClick={() => setShowAnalytics(!showAnalytics)} className="flex items-center gap-2 w-full text-left text-xs font-medium text-slate-300">
              <BarChart3 size={14} />
              Analytics
            </button>
            {showAnalytics && (
              <div className="space-y-3 pl-4 border-l border-slate-700">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[#FF3B30]" /></div>
                ) : analyticsSummary ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 rounded-lg bg-black/40 border border-slate-700">
                        <p className="text-[10px] text-slate-500 mb-0.5">Total views</p>
                        <p className="text-lg font-bold text-[#FF3B30]">{analyticsSummary.totalViews}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-black/40 border border-slate-700">
                        <p className="text-[10px] text-slate-500 mb-0.5">Unique visitors</p>
                        <p className="text-lg font-bold text-blue-400">{analyticsSummary.uniqueVisitors}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-black/40 border border-slate-700">
                        <p className="text-[10px] text-slate-500 mb-0.5">Hotspot clicks</p>
                        <p className="text-lg font-bold text-green-400">{analyticsSummary.totalHotspotClicks}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-black/40 border border-slate-700">
                        <p className="text-[10px] text-slate-500 mb-0.5">Avg. time</p>
                        <p className="text-lg font-bold text-cyan-400">{analyticsSummary.avgTimePerView < 60 ? `${analyticsSummary.avgTimePerView}s` : `${Math.floor(analyticsSummary.avgTimePerView / 60)}m`}</p>
                      </div>
                    </div>
                    {analyticsSummary.viewsByDay.length > 0 && (
                      <div>
                        <p className="text-[10px] text-slate-500 mb-2">Views — last 30 days</p>
                        <div className="flex items-end gap-[2px] h-16">
                          {analyticsSummary.viewsByDay.map((d, i) => {
                            const max = Math.max(1, ...analyticsSummary.viewsByDay.map((x) => x.count));
                            return (
                              <div key={i} title={`${d.date}: ${d.count}`} className="flex-1 bg-[#FF3B30]/60 rounded-t-sm min-h-[2px]" style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {analyticsSummary.totalViews === 0 && (
                      <p className="text-[10px] text-slate-500 py-2">No views yet. Share your plan to start tracking!</p>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] text-slate-500 py-2">Unable to load analytics.</p>
                )}
              </div>
            )}
          </div>

          {/* Viewer style & branding — colours, logo, layout (rooms list position) */}
          <div className="space-y-2">
            <button type="button" onClick={() => setShowViewerSettings(!showViewerSettings)} className="flex items-center gap-2 w-full text-left text-xs font-medium text-slate-300">
              <Palette size={14} />
              Viewer style & branding
            </button>
            {!showViewerSettings && (
              <p className="text-[10px] text-slate-500 pl-6">Colours, logo, and layout (rooms list left/right/bottom/minimal)</p>
            )}
            {showViewerSettings && (
              <div className="space-y-2 pl-4 border-l border-slate-700">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-500 w-20 shrink-0">Primary</label>
                  <input type="color" value={viewerSettings.primary_color ?? '#FF3B30'} onChange={(e) => handleViewerSettingsUpdate({ primary_color: e.target.value })} className="w-8 h-6 rounded cursor-pointer border border-slate-700" />
                  <input type="text" value={viewerSettings.primary_color ?? '#FF3B30'} onChange={(e) => handleViewerSettingsUpdate({ primary_color: e.target.value })} className="flex-1 px-2 py-1 bg-black border border-slate-700 rounded text-[10px] text-slate-300" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-500 w-20 shrink-0">Background</label>
                  <input type="color" value={viewerSettings.background_color ?? '#1a1a1a'} onChange={(e) => handleViewerSettingsUpdate({ background_color: e.target.value })} className="w-8 h-6 rounded cursor-pointer border border-slate-700" />
                  <input type="text" value={viewerSettings.background_color ?? '#1a1a1a'} onChange={(e) => handleViewerSettingsUpdate({ background_color: e.target.value })} className="flex-1 px-2 py-1 bg-black border border-slate-700 rounded text-[10px] text-slate-300" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-500 w-20 shrink-0">Accent</label>
                  <input type="color" value={viewerSettings.accent_color ?? '#2F2F2F'} onChange={(e) => handleViewerSettingsUpdate({ accent_color: e.target.value })} className="w-8 h-6 rounded cursor-pointer border border-slate-700" />
                  <input type="text" value={viewerSettings.accent_color ?? '#2F2F2F'} onChange={(e) => handleViewerSettingsUpdate({ accent_color: e.target.value })} className="flex-1 px-2 py-1 bg-black border border-slate-700 rounded text-[10px] text-slate-300" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-1">Logo</label>
                  {viewerSettings.logo_url ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <img src={viewerSettings.logo_url} alt="Logo" className="h-8 object-contain" />
                      <button type="button" onClick={() => handleViewerSettingsUpdate({ logo_url: null })} className="text-[10px] text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => logoInputRef.current?.click()} className="w-full py-1.5 border border-dashed border-slate-600 rounded text-[10px] text-slate-400 hover:border-slate-500">Upload logo</button>
                  )}
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-500 w-14 shrink-0">Logo size</label>
                  <div className="flex gap-1">
                    {(['small', 'medium', 'large'] as const).map((size) => (
                      <button key={size} type="button" onClick={() => handleViewerSettingsUpdate({ logo_size: size })} className={`px-2 py-1 rounded text-[10px] font-medium capitalize ${viewerSettings.logo_size === size || (size === 'small' && !viewerSettings.logo_size) ? 'bg-[#FF3B30] text-white' : 'bg-black/40 text-slate-400 hover:bg-black/60'}`}>{size}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-2">Floor plan style</label>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => handleViewerSettingsUpdate({ show_rooms_list: true, rooms_list_position: 'left' })}
                      className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left border transition-colors ${viewerSettings.show_rooms_list !== false && (viewerSettings.rooms_list_position ?? 'left') === 'left' ? 'bg-[#FF3B30]/20 border-[#FF3B30] text-white' : 'bg-black/40 border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                    >
                      <span className="text-xs font-medium">Rooms list (left)</span>
                      <span className="text-[10px] opacity-80">Sidebar on the left; click a room to jump.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewerSettingsUpdate({ show_rooms_list: true, rooms_list_position: 'right' })}
                      className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left border transition-colors ${viewerSettings.show_rooms_list !== false && viewerSettings.rooms_list_position === 'right' ? 'bg-[#FF3B30]/20 border-[#FF3B30] text-white' : 'bg-black/40 border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                    >
                      <span className="text-xs font-medium">Rooms list (right)</span>
                      <span className="text-[10px] opacity-80">Sidebar on the right; click a room to jump.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewerSettingsUpdate({ show_rooms_list: true, rooms_list_position: 'bottom' })}
                      className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left border transition-colors ${viewerSettings.show_rooms_list !== false && viewerSettings.rooms_list_position === 'bottom' ? 'bg-[#FF3B30]/20 border-[#FF3B30] text-white' : 'bg-black/40 border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                    >
                      <span className="text-xs font-medium">Rooms list (bottom)</span>
                      <span className="text-[10px] opacity-80">Horizontal strip below the floor plan.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewerSettingsUpdate({ show_rooms_list: false })}
                      className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg text-left border transition-colors ${viewerSettings.show_rooms_list === false ? 'bg-[#FF3B30]/20 border-[#FF3B30] text-white' : 'bg-black/40 border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                    >
                      <span className="text-xs font-medium">Floor plan only</span>
                      <span className="text-[10px] opacity-80">Just the floor plan, logo and map icon; no rooms list.</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-1">Watermark (behind logo)</label>
                  {viewerSettings.logo_watermark_url ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <img src={viewerSettings.logo_watermark_url} alt="Watermark" className="h-6 object-contain opacity-60" />
                      <button type="button" onClick={() => handleViewerSettingsUpdate({ logo_watermark_url: null })} className="text-[10px] text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => watermarkInputRef.current?.click()} className="w-full py-1.5 border border-dashed border-slate-600 rounded text-[10px] text-slate-400 hover:border-slate-500">Upload watermark</button>
                  )}
                  <input ref={watermarkInputRef} type="file" accept="image/*" className="hidden" onChange={handleWatermarkUpload} />
                  {viewerSettings.logo_watermark_url && (
                    <div className="flex items-center gap-2 mt-1">
                      <label className="text-[10px] text-slate-500 shrink-0">Opacity</label>
                      <input type="range" min="0.05" max="0.6" step="0.05" value={viewerSettings.logo_watermark_opacity ?? 0.2} onChange={(e) => handleViewerSettingsUpdate({ logo_watermark_opacity: Number(e.target.value) })} className="flex-1 h-1.5 accent-red-600" />
                      <span className="text-[10px] text-slate-500 w-6">{Math.round((viewerSettings.logo_watermark_opacity ?? 0.2) * 100)}%</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-slate-500 w-20 shrink-0">Text colour</label>
                  <input type="color" value={viewerSettings.text_color ?? '#ffffff'} onChange={(e) => handleViewerSettingsUpdate({ text_color: e.target.value })} className="w-8 h-6 rounded cursor-pointer border border-slate-700" />
                  <input type="text" value={viewerSettings.text_color ?? '#ffffff'} onChange={(e) => handleViewerSettingsUpdate({ text_color: e.target.value })} className="flex-1 px-2 py-1 bg-black border border-slate-700 rounded text-[10px] text-slate-300" placeholder="#ffffff" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={viewerSettings.show_powered_by !== false} onChange={(e) => handleViewerSettingsUpdate({ show_powered_by: e.target.checked })} className="rounded" />
                  <span className="text-[10px] text-slate-400">Show &quot;Powered by planos.studio&quot;</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="pt-2 border-t space-y-2" style={{ borderColor: '#3d3d3d' }}>
          <button onClick={handleSave} disabled={saving} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 flex items-center justify-center gap-2" style={{ backgroundColor: '#FF3B30' }}>
            <Save size={16} /> {saving ? 'Saving...' : 'Save Draft'}
          </button>
          {onProjectDelete && (
            <button onClick={() => setShowDeleteConfirm(true)} className="w-full py-2 rounded-xl text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30 flex items-center justify-center gap-2 transition-colors">
              <Trash2 size={16} /> Delete project
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm p-6 rounded-2xl border" style={{ backgroundColor: '#2F2F2F', borderColor: '#3d3d3d' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Delete project?</h3>
            <p className="text-sm text-slate-400 mb-6">This cannot be undone. The project and all its data will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-black/30 transition-colors">
                Cancel
              </button>
              <button onClick={() => { setShowDeleteConfirm(false); handleDelete(); }} className="flex-1 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-500 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showHostingModal && (
        <HostingModal
          project={project}
          user={user}
          slotCheck={hostingModalSlotCheck}
          onClose={() => { setShowHostingModal(false); setHostingModalSlotCheck(null); }}
          onSuccess={(updates, creditsRemaining) => {
            onProjectUpdate({ ...project, ...updates });
            if (typeof creditsRemaining === 'number') onCreditsChange(creditsRemaining - user.credits);
            setShowHostingModal(false);
            setHostingModalSlotCheck(null);
          }}
          onOpenCredits={onOpenCredits}
          onOpenPlans={onOpenPlans}
        />
      )}

      {/* Map canvas (map mode) or Floor plan canvas (floorplan mode) */}
      <div className="flex-1 rounded-2xl overflow-hidden border min-h-[280px] sm:min-h-[360px] lg:min-h-[400px] min-w-0 flex flex-col" style={{ borderColor: '#3d3d3d' }}>
        {isFloorplanMode ? (
          /* Floor plan only: pan/zoom via react-zoom-pan-pinch, double-click to add hotspots */
          <div className="flex-1 min-h-[280px] sm:min-h-[360px] lg:min-h-[400px] w-full relative bg-black" style={{ overflow: 'hidden' }}>
            {floorplanUrl ? (
              /* Single full-size container so the transform wrapper has a parent with definite dimensions */
              <div className="absolute inset-0 w-full h-full min-h-[280px] sm:min-h-[360px] lg:min-h-[400px]" style={{ pointerEvents: 'auto' }}>
                <TransformWrapper
                  initialScale={1}
                  minScale={0.5}
                  maxScale={4}
                  centerOnInit
                  limitToBounds={false}
                  doubleClick={{ mode: 'reset', excluded: ['button', 'planos-hotspot', 'planos-floor-content', 'planos-zoom-control'] }}
                  wheel={{ step: 0.08, excluded: ['button', 'planos-hotspot', 'planos-zoom-control'] }}
                  pinch={{ step: 5, excluded: ['button', 'planos-hotspot', 'planos-zoom-control'] }}
                  panning={{ disabled: false, velocityDisabled: true, activationKeys: [], excluded: ['planos-hotspot', 'planos-zoom-control'], allowLeftClickPan: true }}
                >
                  {({ zoomIn, zoomOut, resetTransform }) => (
                    <>
                      <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-1 rounded-lg border border-white/20 bg-black/60 p-1 shadow-lg backdrop-blur-sm">
                        <button type="button" onClick={(e) => { e.stopPropagation(); zoomIn(); }} className="planos-zoom-control planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Zoom in">
                          <ZoomIn size={20} style={{ color: '#FF3B30' }} />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); zoomOut(); }} className="planos-zoom-control planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Zoom out">
                          <ZoomOut size={20} style={{ color: '#FF3B30' }} />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); resetTransform(); }} className="planos-zoom-control planos-hotspot p-2 rounded-md text-white hover:bg-white/20 transition-colors" title="Reset view">
                          <RotateCcw size={18} style={{ color: '#FF3B30' }} />
                        </button>
                      </div>
                      <TransformComponent
                        wrapperClass="planos-pan-wrapper"
                        wrapperStyle={{ position: 'absolute', inset: 0, width: '100%', height: '100%', minWidth: '100%', minHeight: '100%', overflow: 'hidden', touchAction: 'none', cursor: 'grab', userSelect: 'none' }}
                        contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '100%', minHeight: '100%' }}
                      >
                      <div
                        className="planos-floor-content relative inline-block max-w-full max-h-full select-none"
                        style={{ cursor: 'crosshair', userSelect: 'none' }}
                        onPointerDown={handleFloorPlanPointerDown}
                        onPointerUp={handleFloorPlanPointerUp}
                        onPointerLeave={() => { floorPlanPointerDown.current = null; }}
                      >
                        <img
                          src={floorplanUrl}
                          alt="Floor plan"
                          className="max-w-full max-h-full object-contain block pointer-events-none"
                          draggable={false}
                          style={{ maxHeight: 'calc(100vh - 8rem)' }}
                        />
                        {hotspots
                          .filter((h) => h.x_pct != null && h.y_pct != null)
                          .map((h) => {
                            const isSelectedHotspot = h.id === selectedId;
                            return (
                              <button
                                key={h.id}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedId(h.id); }}
                                className={`planos-hotspot absolute w-6 h-6 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer text-white ${isSelectedHotspot ? 'overflow-visible' : 'overflow-hidden'}`}
                                style={{
                                  left: `${h.x_pct!}%`,
                                  top: `${h.y_pct!}%`,
                                  transform: 'translate(-50%, -50%)',
                                  backgroundColor: h.icon_color ?? (isSelectedHotspot ? '#FF3B30' : '#dc2626'),
                                  borderColor: '#fff',
                                }}
                              >
                                {isSelectedHotspot && (
                                  <span
                                    className="absolute inset-0 rounded-full animate-ping pointer-events-none"
                                    style={{ backgroundColor: h.icon_color ?? '#FF3B30', opacity: 0.55 }}
                                  />
                                )}
                                <span className="relative z-[1] flex w-full h-full items-center justify-center rounded-full overflow-hidden">
                                  <EditorHotspotIcon type={h.icon_type} iconUrl={h.icon_url} size={12} />
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </TransformComponent>
                  </>
                )}
                </TransformWrapper>
              </div>
            ) : null}
            {floorplanUrl ? (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/50 text-xs text-white/80 pointer-events-none">
                Select an image, then click floor plan to add · Drag to pan · Scroll to zoom
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFloorplanDrop}
                onClick={() => !uploadingFloorplan && floorplanInputRef.current?.click()}
                className={`w-full h-full flex flex-col items-center justify-center border-2 border-dashed cursor-pointer transition-colors ${dragOver ? 'border-[#FF3B30] bg-[#FF3B30]/10' : 'border-slate-600 hover:border-[#FF3B30]/50 hover:bg-white/5'}`}
              >
                {uploadingFloorplan ? (
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <Loader2 size={32} className="animate-spin" style={{ color: '#FF3B30' }} />
                    <span>Uploading floor plan...</span>
                  </div>
                ) : (
                  <>
                    <ImageIcon size={64} className="text-slate-500 mb-4" />
                    <p className="text-slate-400 text-lg font-medium mb-1">Drop your floor plan here</p>
                    <p className="text-sm text-slate-500">or click to browse · PNG, JPG, or WebP</p>
                  </>
                )}
              </div>
            )}
          </div>
        ) : MAPS_KEY ? (
          <APIProvider apiKey={MAPS_KEY} libraries={['places']}>
            <Map
              {...(MAP_ID ? { mapId: MAP_ID } : {})}
              defaultCenter={center}
              defaultZoom={19}
              heading={mapHeading}
              gestureHandling="greedy"
              disableDefaultUI
              reuseMaps
              onClick={handleMapClick}
              onIdle={(e: any) => setMapRef(e.map)}
              style={{ width: '100%', height: '100%' }}
            >
              {hotspots.map((h) => (
                <AdvancedMarker key={h.id} position={{ lat: h.lat, lng: h.lng }} onClick={() => setSelectedId(h.id)} title={h.room_label}>
                  <div className="relative">
                    <RadarCone heading={h.heading} />
                    {h.id === selectedId && (
                      <span
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full animate-ping pointer-events-none"
                        style={{ backgroundColor: 'rgba(255, 59, 48, 0.45)' }}
                      />
                    )}
                    <Pin background={h.icon_color ?? (h.id === selectedId ? '#FF3B30' : '#dc2626')} glyphColor="#fff" borderColor="#7f1d1d" />
                  </div>
                </AdvancedMarker>
              ))}
            </Map>
          </APIProvider>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-slate-400 text-sm">
            Set VITE_GOOGLE_MAPS_API_KEY to enable the map editor.
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanosEditor;
